import { Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, storage } from '../firebase';

/**
 * Shared helpers for the documents kept on a `partners/{id}` doc. Used by the
 * CRM Partner Overview (Documents tab, internal side) and the partner member's
 * own My Documents page, so both read and write the exact same shapes.
 */

export const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const DOC_UPLOAD_ACCEPT = '.pdf,.jpg,.jpeg,.png,.csv,.xls,.xlsx,.doc,.docx';

/** Fixed document tags — filter chips and assignable in the doc editor. */
export const DOC_TAGS = [
  { id: 'w9', name: 'W9', color: '#7c3aed' },
  { id: 'coi', name: 'COI', color: '#ea580c' },
  { id: 'receipt', name: 'Receipt', color: '#16a34a' },
  { id: 'invoice', name: 'Invoice', color: '#2563eb' },
  { id: 'contract', name: 'Contract', color: '#dc2626' },
  { id: 'nda', name: 'NDA', color: '#0d9488' },
  { id: 'license', name: 'License', color: '#d97706' },
  { id: 'other', name: 'Other', color: '#6b7280' },
];

export function serializeDocumentsForFirestore(docs) {
  return (docs || []).map((d) => ({
    id: String(d.id || newId()),
    name: String(d.name || 'Document'),
    fileName: String(d.fileName || d.name || ''),
    fileUrl: String(d.fileUrl || ''),
    folderId: String(d.folderId || ''),
    tagIds: Array.isArray(d.tagIds) ? d.tagIds.map(String) : [],
    uploadedByUid: String(d.uploadedByUid || ''),
    uploaderName: String(d.uploaderName || ''),
    uploadedAt:
      d.uploadedAt instanceof Date
        ? Timestamp.fromDate(d.uploadedAt)
        : typeof d.uploadedAt === 'string' && d.uploadedAt
          ? Timestamp.fromDate(new Date(d.uploadedAt))
          : Timestamp.now(),
  }));
}

export function normalizeDocFromPartner(d) {
  const v = d.uploadedAt;
  let uploadedAt = null;
  if (v) {
    if (v.toDate) uploadedAt = v.toDate();
    else if (v instanceof Date) uploadedAt = v;
    else if (typeof v === 'string') {
      const dt = new Date(v);
      uploadedAt = Number.isNaN(dt.getTime()) ? null : dt;
    }
  }
  return {
    id: d.id || newId(),
    name: d.name || '',
    fileName: d.fileName || d.name || '',
    fileUrl: d.fileUrl || '',
    folderId: d.folderId || '',
    tagIds: Array.isArray(d.tagIds) ? d.tagIds.map(String) : [],
    uploadedByUid: d.uploadedByUid || '',
    uploaderName: d.uploaderName || '',
    uploadedAt,
  };
}

export function serializeFoldersForFirestore(folders) {
  return (folders || []).map((f) => ({
    id: String(f.id || newId()),
    name: String(f.name || 'Folder'),
    createdByName: String(f.createdByName || ''),
    createdAt:
      f.createdAt instanceof Date
        ? Timestamp.fromDate(f.createdAt)
        : f.createdAt && typeof f.createdAt.toDate === 'function'
          ? f.createdAt
          : null,
  }));
}

export function normalizeFoldersFromPartner(raw) {
  return Array.isArray(raw)
    ? raw.map((f) => ({
        id: String(f.id || newId()),
        name: String(f.name || 'Folder'),
        createdByName: String(f.createdByName || ''),
        createdAt: f.createdAt && typeof f.createdAt.toDate === 'function' ? f.createdAt.toDate() : null,
      }))
    : [];
}

/** Upload a partner document file; returns its download URL. */
export async function uploadPartnerDocumentFile(partnerId, docId, file) {
  if (!auth.currentUser) throw new Error('You must be signed in to upload files.');
  const safeName = (file.name || 'file').replace(/[^\w.-]+/g, '_');
  const fullPath = `partners/${partnerId}/documents/${docId}/${Date.now()}_${safeName}`;
  const storageRef = ref(storage, fullPath);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}
